import React from 'react';
import { Shield, Users, CheckCircle, Lock } from 'lucide-react';
import { useFadeIn } from '../hooks/useFadeIn';
import './TrustCommunitySection.css';

interface TrustCommunitySectionProps {
  onLoginClick?: () => void;
}

const TrustCommunitySection: React.FC<TrustCommunitySectionProps> = ({ onLoginClick }) => {
  const headerFadeIn = useFadeIn({ delay: 0.1 });
  const featuresFadeIn = useFadeIn({ delay: 0.2 });
  const ctaFadeIn = useFadeIn({ delay: 0.3 });

  const features = [
    {
      icon: Shield,
      title: 'AI Risk Scoring',
      description: 'Detect fake, suspicious, and high-risk users autonomously using multi-signal AI.'
    },
    {
      icon: Lock,
      title: 'Privacy-First Design',
      description: 'Protect sensitive data by default while still giving moderators the signals they need.'
    },
    {
      icon: Users,
      title: 'Custom Review Workflows',
      description: 'Customize approval pipelines and streamline context to review users.'
    },
    {
      icon: CheckCircle,
      title: 'Faster Trust, Faster Growth',
      description: 'Reduce moderation bottlenecks and focus on growing your community.'
    }
  ];

  return (
    <section className="trust-community-section landing-section">
      <div className="trust-container landing-container">
        <div 
          ref={headerFadeIn.ref} 
          className={`trust-header ${headerFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${headerFadeIn.delay}s` } as React.CSSProperties}
        >
          <h2 className="trust-title">Build a Safer Community <span className="trust-title-break">with Smarter Moderation</span></h2>
          <p className="trust-subtitle">
            Spend your time shipping features, not screening profiles.
          </p>
        </div>

        <div 
          ref={featuresFadeIn.ref} 
          className={`trust-features-grid ${featuresFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${featuresFadeIn.delay}s` } as React.CSSProperties}
        >
          {features.map((feature, index) => {
            const IconComponent = feature.icon;
            return (
              <div 
                key={index}
                className="trust-feature-card"
                style={{ '--delay': index * 0.1 } as React.CSSProperties}
              >
                <div className="trust-feature-icon">
                  <IconComponent size={20} />
                </div>
                <div className="trust-feature-content">
                  <h3 className="trust-feature-title">{feature.title}</h3>
                  <p className="trust-feature-description">{feature.description}</p>
                </div>
              </div>
            );
          })}
        </div>

        <div 
          ref={ctaFadeIn.ref} 
          className={`trust-cta ${ctaFadeIn.isVisible ? 'fade-in-hero' : ''}`}
          style={{ animationDelay: `${ctaFadeIn.delay}s` } as React.CSSProperties}
        >
          <button className="landing-cta-primary trust-btn" onClick={onLoginClick}>
            Get Started
          </button>
        </div>
      </div>
    </section>
  );
};

export default TrustCommunitySection;

